<template>
  <Modal ref="modal" class="modal-inquiry-download" :actions="actions" @action="handleAction" immediate>
    <h1>Bericht herunterladen</h1>
    <div v-if="!processing" class="downloads">
      <button @click="handleExport('pdf')">
        <span><Icon name="pdf" /> PDF DOKUMENT</span>
        <Icon name="download" />
      </button>
      <button @click="handleExport('docx')">
        <span><Icon name="word" /> WORD DOKUMENT</span>
        <Icon name="download" />
      </button>
      <button @click="handleExport('json')">
        <span><Icon name="json" /> JSON DATEI</span>
        <Icon name="download" />
      </button>
    </div>
    <div v-else class="processing">
      <Loader />
    </div>
  </Modal>
</template>

<script>
  import { Store } from '@/store';
  import Modal from '@/components/ui/Modal';
  import Select from '@/components/ui/inputs/Select';
  import VueUtil from '@/utils/VueUtil';
  import InquiryExportPDF from '@/components/inquiry/InquiryExportPDF';
  import InquiryExportWord from '@/components/inquiry/InquiryExportWord';
  import InquiryDefinitionFunctions from '@/components/inquiry/InquiryDefinitionFunctions';
  import Loader from '@/components/ui/Loader';
  import Icon from '@/components/ui/Icon';
  import JSONDownloader from '../../utils/JSONDownloader';

  export default {
    name: 'ModalInquiryDownload',
    components: {
      Icon,
      Loader,
      Select,
      Modal,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      uuid: {
        type: String,
        default: null,
      },
      savId: {
        type: String,
        default: null,
      },
      structure: {
        type: Array,
        default: null,
      },
      values: {
        type: Object,
        default: null,
      },
      version: {
        type: [String, Number],
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        inquiryid: null,
        exportData: null,
        jsonData: null,
        secondarySignature: null,
        processing: false,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      actions() {
        if (!this.processing) {
          const i18n = this.$i18n;
          return [
            { value: 'cancel', label: i18n.t('labels.cancel'), class: 'secondary' },
          ];
        }
        return [];
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {},
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // async created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    async mounted() {
      const uuid = this.uuid;
      this.inquiryid = this.savId;
      let values = this.values;
      let dataVersion = this.version;
      if (!values) {
        const { data, inquiryid, version } = await this.$sav.api.get(`/inquiry/${uuid}`);
        values = data;
        dataVersion = version;
        this.inquiryid = inquiryid;
      }

      let structure = this.structure;
      if (!structure) {
        structure = await this.createStructure(dataVersion);
      }
      this.jsonData = JSON.stringify({
        savId: this.inquiryid,
        schemaVersion: dataVersion,
        data: values,
      }, null, 2);
      this.exportData = this.prepareExportData(structure, values);
    },
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      prepareExportData(structure, values) {
        const exportData = [];
        structure.forEach((item) => {
          if (item.type === 'section') {
            const groups = [];
            item.children.forEach((g) => {
              const group = {
                label: g.label || null,
                type: g.exportType || null,
                inputs: [],
                export: g.export || null,
              };

              const inputs = this.getAllGroupInputs(g).filter(input => input.for_report === true);
              let doGroupExport = true;
              if (g.exportFunc) {
                doGroupExport = this[g.exportFunc](group, inputs, values);
              }
              if (doGroupExport && inputs.length > 0) {
                inputs.forEach((input) => {
                  if (input.id === '8bc96d71-7185-4734-97f1-08ec0f3d690a') {
                    this.secondarySignature = values[input.id] || null;
                  } else if (input.export !== false) {
                    const parsed = {
                      id: input.id,
                      type: input.component,
                      label: input.props.label,
                      export: input.props.export || null,
                      exportStyle: input.exportStyle || null,
                      fallback: input.props.fallback !== null ? input.props.fallback : null,
                      simplified: input.props.simplified || false,
                      value: this.getComponentExportValue(input.component, input.props, values[input.id]),
                    };

                    if (parsed.type === 'diagnosis') {
                      if (parsed.simplified && (parsed.value || []).length > 0) {
                        parsed.value = parsed.value.map(m => ({
                          ...m,
                          diagnosis: 'DSM',
                        }));
                        group.inputs.push(parsed);
                      } else if (!parsed.simplified) {
                        group.inputs.push(parsed);
                      }
                    } else {
                      group.inputs.push(parsed);
                    }
                  }
                });
                if (group.inputs.length > 0) {
                  groups.push(group);
                }
              }
            });
            if (groups.length > 0) {
              exportData.push({ heading: item.heading, groups, export: item.export || null });
            }
          }
        });
        return exportData;
      },

      //----------------------------------
      // Helper Methods
      //----------------------------------
      async createStructure(version) {
        const fielddata = this.$i18n.t(`fielddata_versions.${version}`);
        for (let s = 0, slen = fielddata.length; s < slen; s += 1) {
          const section = fielddata[s];
          section.index = s;
          const sectionChildren = section?.children || [];

          for (let c = 0, clen = sectionChildren.length; c < clen; c += 1) {
            const group = sectionChildren[c];
            const groupChildren = group?.children || [];

            for (let g = 0, glen = groupChildren.length; g < glen; g += 1) {
              const comp = groupChildren[g];
              if (comp.type === 'input') {
                if (comp?.props.optionsFunc) {
                  const fn = InquiryDefinitionFunctions[comp.props.optionsFunc];
                  if (fn) {
                    // eslint-disable-next-line no-await-in-loop
                    const { options } = await fn() || null;
                    if (options) {
                      comp.props.options = options;
                    }
                  }
                }
              }
            }
          }
        }
        return fielddata;
      },
      getAllGroupInputs(group) {
        const inputs = [];
        group.children.forEach((child) => {
          if (child.type === 'input') {
            inputs.push(child);
          }
        });
        return inputs;
      },
      getComponentExportValue(type, props, value) {
        switch (type) {
          case 'select': {
            if (props.options) {
              const option = props.options.find(o => o.value === value);
              if (option) {
                return option.label;
              }
            }
            return value;
          }
          case 'checkbox-group': {
            const out = [];
            const val = Array.isArray(value) ? value : [value];
            (val || []).forEach((v) => {
              const option = props.options.find(o => o.value === v);
              if (option) {
                out.push(option.label);
              } else if (v && typeof v === 'string' && v.includes('other-')) {
                const label = v.replace('other-', '').replace(/_/g, ' ');
                out.push(label);
              }
            });
            return out.join(', ');
          }
          case 'radio-group': {
            const option = props.options.find(o => o.value === value);
            if (option) {
              return option.label;
            }
            break;
          }
          case 'radio-matrix': {
            return value;
          }
          case 'radio-matrix-advanced': {
            return value;
          }
          case 'diagnosis': {
            const values = (value || [])
              // .filter(v => v.description !== null || v.specialist !== null)
              .map(v => ({
                ...v,
                diagnosis: this.findDiagnosisLabelByValue(v.diagnosis, props.codes) || v.diagnosis,
              }));
            if (values.length > 0) {
              return values;
            }
            return null;
          }
          case 'datepicker': {
            if (value) {
              let [year, month, day] = value.split('-');
              year = year === undefined ? 'XXXX' : year;
              month = month === undefined ? 'XX' : month;
              day = day === undefined ? 'XX' : day;
              return `${day}.${month}.${year}` || '';
            }
            return '';
          }
          default: {
            return value || '';
          }
        }
        return '';
      },

      findDiagnosisLabelByValue(value, arr = []) {
        const category = arr.find(c => c.items.some(item => item.value === value));
        return category ? category.items.find(item => item.value === value)?.label || null : null;
      },


      exportIfNotEmpty(group, inputs, values) {
        const inputsWithContent = inputs.filter((input) => {
          const value = values[input.id];
          if (Array.isArray(value) && value.length > 0) {
            return true;
          }
          return typeof value === 'string' && value !== '';
        });
        return inputsWithContent.length > 0;
      },

      getJsonFilename(exportData, inquiryid, file_ext) {
        const findObjectById = targetId => exportData.flatMap(obj => obj.groups || [])
          .flatMap(group => group.inputs || [])
          .find(input => input.id === targetId);

        let firstname = findObjectById('9490f6ea-220b-4d71-86b5-b6129887d008');
        firstname = (firstname) ? `${firstname.value} ` : '';
        const lastname = findObjectById('973834ac-3158-46be-8285-820d6545ac99');
        const fullname = (lastname) ? `${firstname}${lastname.value} - ` : '';

        return `SAV JSON - ${fullname}${inquiryid}.${file_ext}`;
      },

      //----------------------------------
      // Event Handlers
      //----------------------------------
      async handleExport(type) {
        this.processing = true;
        const { signature, frontpage } = await this.$sav.api.post('/inquiry/export-assets', null, {
          signature: this.secondarySignature,
          branch: Store.branch.uuid,
        });

        if (type === 'pdf') {
          const instance = VueUtil.createComponent(InquiryExportPDF, document.body, {
            propsData: {
              id: this.inquiryid,
              exportData: this.exportData,
              secondarySignature: signature,
              frontpage,
            },
          });
          instance.$once('done', () => {
            this.$refs.modal.close();
          });
        } else if (type === 'docx') {
          await InquiryExportWord.export(this.inquiryid, this.exportData, signature, frontpage);
          this.$refs.modal.close();
        } else if (type === 'json') {
          const filename = this.getJsonFilename(this.exportData, this.inquiryid, 'json');
          JSONDownloader.download(this.jsonData, filename);
          this.$refs.modal.close();
        }
      },

      async handleAction() {
        this.$emit('done');
        this.$refs.modal.close();
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.modal-inquiry-download {
  h1 {
    margin-bottom: 12px;
  }

  .downloads {
    display: flex;
    flex-direction: column;
    gap: 12px;
    text-align: center;
    margin-top: 24px;

    span {
      display: block;
      padding: 24px 0;
      @include font-body-small;
    }

    button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      color: $color-black-alpha-60;
      outline: 0;
      border-radius: 4px;
      padding: 6px 12px;
      border: 0;
      height: 42px;
      min-width: 436px;
      cursor: pointer;

      & > span {
        @include font-button;
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        & > svg {
          width: 32px;
          height: 32px;
          fill: currentColor;
        }
      }

      & > svg {
        width: 18px;
        height: 18px;

        & > path {
          fill: currentColor;
        }
      }

      &:hover {
        background-color: $color-blue-alpha-10;
        color: $color-black-alpha-80;
      }
    }
  }

  .processing {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 150px;
  }
}
</style>
